/**
 * Report rows: each dataset reuses the exact builder Overview/Analytics or
 * Automation's own workflows use, so a number in a report always agrees with
 * the page it came from — including a paused workflow's activity, since the
 * caller passes in the same (override-applied) workflows Automation reads.
 * Rows are pre-formatted for display, and the CSV export reuses those same
 * strings rather than re-deriving them.
 */

import type { Workflow } from '@/api/automation'
import type { ReportDataset, ReportRow } from '@/api/reports'
import { formatCurrency, formatInteger, formatPercent, formatRatio } from '@/design/format'
import { formatDateKey, resolvePeriod, type GlobalFilters } from '@/lib/filters'

import { buildActivity } from '../automation/engine'
import type { DemoDataset } from '../generators/engine'
import { buildChannels, buildTopCompetitions } from '../generators/overview'

const PACING_LABELS = { ahead: 'Ahead', on_track: 'On track', behind: 'Behind' } as const
const ROW_LIMIT = 50

export function reportRowsFor(
  datasetKey: ReportDataset,
  dataset: DemoDataset,
  workflows: readonly Workflow[],
  filters: GlobalFilters,
  today: string,
): ReportRow[] {
  const money = { currency: dataset.tenant.currency, locale: dataset.tenant.locale }

  switch (datasetKey) {
    case 'competitions': {
      const { data } = buildTopCompetitions(dataset, filters, ROW_LIMIT)
      return data.map((row) => ({
        name: row.entity.label,
        category: row.category,
        revenue: formatCurrency(row.revenue, money),
        ticketsSold: formatInteger(row.ticketsSold, money),
        sellThrough: formatPercent(row.sellThrough),
        pacing: PACING_LABELS[row.pacing],
        closesOn: formatDateKey(row.closesOn, true),
      }))
    }
    case 'channels': {
      const { data } = buildChannels(dataset, filters)
      return data.slice(0, ROW_LIMIT).map((row) => ({
        name: row.entity.label,
        kind: row.kind === 'digital' ? 'Digital' : 'Offline',
        spend: formatCurrency(row.spend, money),
        attributedRevenue: formatCurrency(row.attributedRevenue, money),
        orders: formatInteger(row.orders, money),
        roas: row.roas === null ? '—' : formatRatio(row.roas),
      }))
    }
    case 'automation_activity': {
      const period = resolvePeriod(filters, dataset.now).current
      const activity = buildActivity(dataset.tenant, workflows, today).filter(
        (entry) => entry.at.slice(0, 10) >= period.from && entry.at.slice(0, 10) <= period.to,
      )
      return activity.slice(0, ROW_LIMIT).map((entry) => ({
        workflowName: entry.workflowName,
        at: formatDateKey(entry.at.slice(0, 10), true),
        outcome: entry.outcome[0]?.toUpperCase() + entry.outcome.slice(1),
        summary: entry.summary,
      }))
    }
  }
}
